import React, { useEffect, useRef } from "react";

interface ProgressbarViewProps {
  percent: number;
  type?: "bar" | "radial";
  animate?: boolean;
  size?: number; // 1–10
  label?: string;
  hideValue?: boolean;
}

export const getProgressColorStyle = (percent: number) => {
  if (percent >= 100) return "c5";
  if (percent >= 75) return "c4";
  if (percent >= 50) return "c3";
  if (percent >= 25) return "c2";
  return "c1";
};

export const ProgressbarView: React.FC<ProgressbarViewProps> = ({
  percent,
  type = "bar",
  animate = false,
  size = 5,
  label,
  hideValue = false,
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const circleRef = useRef<SVGCircleElement>(null);

  const clampedSize = Math.max(1, Math.min(size || 5, 10));
  const strokeDashOffset = 45 * Math.PI * 2 * (1 - percent / 100);

  useEffect(() => {
    if (type === "bar" && barRef.current) {
      barRef.current.style.width = `${percent}%`;
    }
    if (type === "radial" && circleRef.current) {
      circleRef.current.style.strokeDashoffset = `${strokeDashOffset}%`;
    }
  }, [percent, type]);

  if (type === "radial") {
    return (
      <div
        className={`eldml-progress progress-radial size-${clampedSize} ${
          animate ? "animate" : ""
        } ${getProgressColorStyle(percent)} ${hideValue ? "hidden-value" : ""}`}
      >
        <div className="progress-content">
          <svg
            className="progress-svg"
            width="100%"
            height="100%"
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle className="track" cx="50" cy="50" r="45" />
            <circle
              ref={circleRef}
              className={`bar ${getProgressColorStyle(percent)}`}
              cx="50"
              cy="50"
              r="45"
              strokeDasharray={`${45 * Math.PI * 2}`}
              strokeDashoffset={`${45 * Math.PI * 2}`}
            />
            {!hideValue && (
              <text
                className="progress-value"
                x="50%"
                y={label ? "42%" : "50%"}
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {percent}%
              </text>
            )}
            {label && (
              <foreignObject
                className="progress-label-html"
                width="100%"
                height="2em"
                x="0"
                y={hideValue ? "50%" : "55%"}
                requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
              >
                <div title={label}>{label}</div>
              </foreignObject>
            )}
          </svg>
        </div>
      </div>
    );
  }

  // Domyślny: type === "bar"
  return (
    <div
      className={`eldml-progress progress type-bar ${getProgressColorStyle(percent)} ${
        animate ? "animate" : ""
      }`}
    >
      <div
        ref={barRef}
        className="progress-bar"
        role="progressbar"
        style={{ width: "0%" }}
      >
        {percent}%
      </div>
    </div>
  );
};
